import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Clock, CheckCircle2, Users, Calendar, Trophy, BookOpen, Heart, MessageCircle, Play, TrendingUp, Zap, Timer, Youtube, User, LayoutGrid, HelpCircle, Layers, Bell, Check, Infinity } from "lucide-react";
import { Link } from "react-router-dom";

const IndexCn = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="CSW - 线上自修室 | 免费虚拟学习室 | Online Workspace | Productivity Tool | Study Stream"
        description="免费线上自修室，提升专注力与学习习惯。24小时开放、番茄钟计时器、与全球学生一起学习。"
        keywords="线上自修室, 虚拟学习空间, 在线学习, 学习直播, 番茄钟"
        canonicalUrl="/cn/"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
          {/* Animated Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
              {/* Left Content */}
              <div className="space-y-8 animate-fade-in">
                <div className="space-y-4">
                  <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-foreground leading-tight">
                    线上自修室
                  </h1>
                  <p className="text-2xl sm:text-3xl text-muted-foreground font-light">
                    提升专注力与学习习惯的空间
                  </p>
                </div>

                <p className="text-lg text-muted-foreground leading-relaxed">
                  线上自修室，特别是CSW（Chill Study Work），是彻底改变我学习方式的工具。在家里很难集中注意力，时间管理也很困难，但通过线上自修室的功能和规则，我的学习效率有了显著提升。
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    asChild
                    size="lg"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 transition-all hover:scale-105"
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      立即加入
                    </a>
                  </Button>
                  <Button 
                    asChild
                    variant="outline"
                    size="lg"
                    className="font-semibold px-8 group"
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                      <Play className="w-5 h-5 group-hover:scale-110 transition-transform" />
                      免费体验
                    </a>
                  </Button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-8 pt-4">
                  <div>
                    <div className="text-4xl font-bold text-foreground">100k+</div>
                    <div className="text-muted-foreground mt-1">学习时长达成</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-foreground">8k+</div>
                    <div className="text-muted-foreground mt-1">任务完成</div>
                  </div>
                </div>
              </div>

              {/* Right Video */}
              <div className="relative animate-fade-in" style={{ animationDelay: "0.2s" }}>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl border-2 border-border/50 bg-card/50 backdrop-blur-sm">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 opacity-50" />
                  <video 
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                    className="w-full h-auto relative z-10"
                  >
                    <source src="/videos/productivity-demo.mp4" type="video/mp4" />
                  </video>
                </div>
                {/* Floating Stats Cards */}
                <div className="absolute -bottom-6 -left-6 bg-card/90 backdrop-blur-sm border-2 border-border/50 rounded-xl p-4 shadow-lg animate-fade-in" style={{ animationDelay: "0.4s" }}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-primary-foreground" />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">效率提升</div>
                      <div className="text-xl font-bold text-foreground">+40%</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Modes Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 space-y-4">
              <div className="inline-block px-4 py-2 bg-primary/10 rounded-full mb-4">
                <span className="text-sm font-semibold text-primary uppercase tracking-wider">认识新朋友</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-foreground">
                无需社交压力
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                在没有沟通压力的情况下，自然地与他人建立联系。
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Chill Mode */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all hover:scale-105 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-600/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Heart className="w-8 h-8 text-blue-500" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">Chill</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  在轻松的氛围中悠闲地学习。
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-blue-500" />
                    背景音乐
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-blue-500" />
                    自由聊天
                  </li>
                </ul>
              </Card>

              {/* Study Mode */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 border-primary hover:border-primary transition-all hover:scale-105 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <BookOpen className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">Study</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  体验专注的学习环境。
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    番茄钟计时器
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    学习记录
                  </li>
                </ul>
              </Card>

              {/* Work Mode */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-purple-500/50 transition-all hover:scale-105 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-600/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Zap className="w-8 h-8 text-purple-500" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">Work</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  发挥最大生产力的完美空间。
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-purple-500" />
                    任务管理
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-purple-500" />
                    成果追踪
                  </li>
                </ul>
              </Card>
            </div>

            <div className="text-center mt-12">
              <Button 
                asChild
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8"
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  开始体验！
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 space-y-4">
              <h2 className="text-4xl sm:text-5xl font-bold text-foreground">
                线上自修室的核心功能
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                CSW提供舒适的环境来提高学习效率。我们配备了方便的功能和工具，让您可以记录和回顾学习进度。
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Feature 1 */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all hover:scale-105 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <BookOpen className="w-8 h-8 text-primary-foreground" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">学习记录与回顾</h3>
                <p className="text-muted-foreground leading-relaxed">
                  记录学习的开始和结束时间，直观地查看学习时长。通过这个功能，您可以轻松了解自己的学习节奏和专注程度。
                </p>
              </Card>

              {/* Feature 2 */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all hover:scale-105 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <MessageCircle className="w-8 h-8 text-primary-foreground" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">自由自习空间与专属聊天</h3>
                <p className="text-muted-foreground leading-relaxed">
                  在任何人都可以自由使用的自习空间中，通过表情反应和聊天与其他用户互动。这种交流对保持学习动力非常有帮助。
                </p>
              </Card>

              {/* Feature 3 */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all hover:scale-105 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Trophy className="w-8 h-8 text-primary-foreground" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">激励系统</h3>
                <p className="text-muted-foreground leading-relaxed">
                  根据学习时间和完成的任务获得奖励，让学习变得有趣。此外，我们还提供各种帮助保持专注的程序。
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Alone but Never Lonely Section */}
        <section className="py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-secondary/5 to-background" />
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto items-center">
              <Card className="p-12 bg-card/50 backdrop-blur-sm border-2 hover:border-primary/50 transition-all">
                <h2 className="text-4xl font-bold text-foreground mb-4">独处，但不孤单</h2>
                <p className="text-xl text-muted-foreground">
                  这是一个特别适合内向人士的环境，您可以安静地学习，同时自然地与他人建立联系。
                </p>
              </Card>

              <Card className="p-12 bg-card/50 backdrop-blur-sm border-2 hover:border-primary/50 transition-all">
                <div className="flex items-center gap-4 mb-4">
                  <Heart className="w-12 h-12 text-primary" />
                  <h2 className="text-4xl font-bold text-foreground">静谧时光</h2>
                </div>
                <p className="text-xl text-muted-foreground">
                  在需要专注的时刻，为您提供安静的环境。
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Impact Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 space-y-4">
              <div className="inline-block px-4 py-2 bg-primary/10 rounded-full mb-4">
                <span className="text-sm font-semibold text-primary uppercase tracking-wider">高效学习</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-foreground">
                学习习惯与动力的提升
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                使用CSW后，我在培养学习习惯方面取得了显著效果。
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Impact 1 */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all">
                <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-6">
                  <Calendar className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">生活习惯养成</h3>
                <p className="text-muted-foreground">
                  每天在固定时间开始学习，日常生活变得更加规律。
                </p>
              </Card>

              {/* Impact 2 */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all">
                <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-6">
                  <Users className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">相互监督保持专注</h3>
                <p className="text-muted-foreground">
                  与其他用户一起学习，保持紧张感和专注力。
                </p>
              </Card>

              {/* Impact 3 */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all">
                <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-6">
                  <Clock className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">学习数据积累</h3>
                <p className="text-muted-foreground">
                  定期回顾自己的学习数据，更容易制定实现目标的计划。
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Features Grid Section */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted via-background to-background" />
          
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 space-y-4 animate-fade-in">
              <h2 className="font-display text-4xl sm:text-5xl font-bold">
                如何提高<span className="text-gradient">生产力</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                选择适合您的模式，释放全部潜能
              </p>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Users, title: "小组模式", description: "与世界各地的伙伴一起学习。加入虚拟房间，保持学习动力。", gradient: "from-primary/20 to-primary/5", iconColor: "text-primary", href: "/group-study/" },
                { icon: Timer, title: "计时器模式", description: "使用内置番茄钟提高专注力。聪明地工作。", gradient: "from-secondary/20 to-secondary/5", iconColor: "text-secondary", href: "/study-timer/" },
                { icon: Youtube, title: "YT模式", description: "工作时观看教育内容。持续学习，不断成长。", gradient: "from-accent/20 to-accent/5", iconColor: "text-accent", href: "/study-stream/" },
                { icon: User, title: "单人模式", description: "在个人工作空间中专注。隐私与效率并存。", gradient: "from-muted-foreground/20 to-muted-foreground/5", iconColor: "text-foreground", href: "/online-workspace/" },
                { icon: LayoutGrid, title: "任务", description: "使用看板整理工作。拖放操作，征服待办事项。", gradient: "from-primary/20 to-primary/5", iconColor: "text-primary", href: "/task-management/" },
                { icon: HelpCircle, title: "测验", description: "通过互动测验检验知识。更聪明地学习。", gradient: "from-secondary/20 to-secondary/5", iconColor: "text-secondary", href: "/quizzes/" },
                { icon: Layers, title: "闪卡", description: "使用数字闪卡掌握任何科目。完美适用于记忆和复习。", gradient: "from-accent/20 to-accent/5", iconColor: "text-accent", href: "/flash-cards/" },
                { icon: Bell, title: "提醒", description: "不错过任何截止日期。设置智能提醒，让学习步入正轨。", gradient: "from-muted-foreground/20 to-muted-foreground/5", iconColor: "text-foreground", href: "/reminders/" },
              ].map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <Link key={feature.title} to={feature.href} onClick={() => window.scrollTo(0, 0)}>
                    <Card
                      className="group relative overflow-hidden border-border bg-card hover:bg-card/80 p-8 transition-all duration-300 hover:scale-105 hover:shadow-2xl animate-fade-in h-full"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                      <div className="relative z-10 space-y-4">
                        <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                          <Icon className={`h-7 w-7 ${feature.iconColor}`} />
                        </div>
                        <h3 className="text-xl font-bold text-foreground">{feature.title}</h3>
                        <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                      </div>
                      <div className="absolute -bottom-2 -right-2 w-24 h-24 bg-primary/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Study Stream Section */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
          <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[100px] animate-glow-pulse" />
          <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-[100px] animate-glow-pulse" style={{ animationDelay: '1.5s' }} />
          
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <Card className="border-2 border-border bg-card/50 backdrop-blur-xl overflow-hidden">
                <div className="grid lg:grid-cols-2 gap-12 p-8 sm:p-12 lg:p-16">
                  <div className="space-y-8 animate-fade-in">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
                      <Infinity className="w-4 h-4 text-primary" />
                      <span className="text-sm font-semibold text-primary">无限制</span>
                    </div>
                    
                    <div className="space-y-4">
                      <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                        学习 <span className="text-gradient">直播</span>
                      </h2>
                      <p className="text-2xl text-muted-foreground font-light">
                        不要为生产力付费
                      </p>
                    </div>
                    
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      随时加入无限制的直播。在虚拟自习室与全球数千名学生建立联系。保持专注、保持动力、保持高效 - 完全免费。
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105" asChild>
                        <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">开始免费直播</a>
                      </Button>
                      <Button size="lg" variant="outline" className="border-2 border-border hover:bg-card font-semibold px-8 py-6 text-lg transition-all hover:scale-105" asChild>
                        <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">了解更多</a>
                      </Button>
                    </div>
                  </div>
                  
                  <div className="space-y-6 animate-fade-in" style={{ animationDelay: '0.2s' }}>
                    <div className="space-y-4">
                      <h3 className="text-2xl font-bold text-foreground mb-6">
                        您需要的一切，<span className="text-gradient">永久免费</span>
                      </h3>
                      
                      <div className="space-y-3">
                        {["无限学习时段", "24小时访问所有房间", "无隐藏费用", "即时加入任何直播", "连接全球社区", "包含高级功能"].map((benefit, index) => (
                          <div key={benefit} className="flex items-center gap-3 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors animate-slide-in" style={{ animationDelay: `${0.3 + index * 0.05}s` }}>
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                              <Check className="w-4 h-4 text-primary-foreground" />
                            </div>
                            <span className="text-foreground font-medium">{benefit}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <Card className="p-6 bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20">
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <p className="text-3xl font-bold text-gradient mb-1">∞</p>
                          <p className="text-sm text-muted-foreground">可用直播</p>
                        </div>
                        <div>
                          <p className="text-3xl font-bold text-gradient mb-1">$0</p>
                          <p className="text-sm text-muted-foreground">费用</p>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-secondary/10" />
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <Card className="max-w-4xl mx-auto p-12 bg-card/80 backdrop-blur-sm border-2 border-primary/20 text-center">
              <h2 className="text-4xl sm:text-5xl font-bold text-foreground mb-6">
                立即开始
              </h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                使用CSW培养高效的学习习惯，实现您的目标。完全免费使用。
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 transition-all hover:scale-105">
                  <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">免费开始</a>
                </Button>
                <Button asChild variant="outline" size="lg" className="font-semibold px-8">
                  <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">了解更多</a>
                </Button>
              </div>
            </Card>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default IndexCn;
